import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from 'prop-types';
import { SketchPicker } from "react-color";
import { fabric } from "fabric";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import '../styles/PosterGenerationPage.css';
import SEO from '../components/SEO';
import SubNav from "../components/subNav";
import { baseUrl } from '../config';



const PosterGenerationPage = ({ initialPosters = [], onPublish, onDownload }) => {
  // Grouped state management
  const [posters, setPosters] = useState(initialPosters);
  const [query, setQuery] = useState("");
  const [selectedPosterIndex, setSelectedPosterIndex] = useState(null);
  const [text, setText] = useState("Movie Title");
  const [color, setColor] = useState("#FFFFFF");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Group related image settings
  const [imageSettings, setImageSettings] = useState({
    brightness: 100,
    contrast: 100,
    sharpness: 0,
    rotation: 0
  });
  
  const [isCropping, setIsCropping] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const canvasRef = useRef(null);

  const initializeCanvas = useCallback(() => {
    const canvas = new fabric.Canvas("poster-canvas");
    const poster = posters[selectedPosterIndex];

    fabric.Image.fromURL(poster.url, (img) => {
      img.set({
        left: 0,
        top: 0,
        selectable: false,
      });
      canvas.clear();
      canvas.add(img);
      canvas.setWidth(img.width);
      canvas.setHeight(img.height);

      // Apply filters
      img.filters = [
        new fabric.Image.filters.Brightness({ brightness: imageSettings.brightness / 100 }),
        new fabric.Image.filters.Contrast({ contrast: imageSettings.contrast / 100 }),
        new fabric.Image.filters.Sharpen({ amount: imageSettings.sharpness / 100 })
      ];
      
      img.applyFilters();
      img.rotate(imageSettings.rotation);

      const textObj = new fabric.Text(text, {
        left: img.width / 2,
        top: img.height - 30,
        fill: color,
        fontSize: 24,
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
        originX: "center",
        selectable: true,
      });
      canvas.add(textObj);
    });

    canvasRef.current = canvas;
  }, [selectedPosterIndex, posters, text, color, imageSettings]);

  // Canvas initialization with cleanup
  useEffect(() => {
    if (selectedPosterIndex !== null) {
      initializeCanvas();
      return () => {
        if (canvasRef.current) {
          canvasRef.current.dispose();
        }
      };
    }
  }, [selectedPosterIndex, text, color, imageSettings, initializeCanvas]);

  // Memoized handlers
  const handleTextChange = useCallback((e) => {
    setText(e.target.value);
  }, []);

  const handleColorChange = useCallback((color) => {
    setColor(color.hex);
  }, []);

  const handleImageSettingChange = useCallback((setting, value) => {
    setImageSettings(prev => ({
      ...prev,
      [setting]: Number(value)
    }));
  }, []);

  const handleSearch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const examplePosters = [
        { url: "https://via.placeholder.com/600x400?text=Poster+1", text: "Poster 1", color: "#FFFFFF" },
        { url: "https://via.placeholder.com/600x400?text=Poster+2", text: "Poster 2", color: "#FFFFFF" },
        { url: "https://via.placeholder.com/600x400?text=Poster+3", text: "Poster 3", color: "#FFFFFF" },
      ];
      setPosters(examplePosters);
      setSelectedPosterIndex(0);
      setText(examplePosters[0].text);
      setColor(examplePosters[0].color);
    } catch (error) {
      setError('Failed to generate posters. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleImageUpload = useCallback((e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const newPoster = {
          url: event.target.result,
          text: "Uploaded Poster",
          color: "#FFFFFF"
        };
        setPosters(prev => [...prev, newPoster]);
        setSelectedPosterIndex(posters.length);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setError('Upload failed: ' + error.message);
    }
  }, [posters.length]);

  const handleDownload = useCallback(() => {
    if (!canvasRef.current) return;
    
    try {
      const dataURL = canvasRef.current.toDataURL({
        format: 'png',
        quality: 1
      });
      
      const link = document.createElement('a');
      link.download = 'poster.png';
      link.href = dataURL;
      link.click();
      
      if (onDownload) {
        onDownload(dataURL);
      }
    } catch (error) {
      setError('Download failed. Please try again.');
    }
  }, [onDownload]);

  const handlePublish = useCallback(() => {
    if (onPublish) {
      onPublish(canvasRef.current.toDataURL());
    }
  }, [onPublish]);

  const handleCrop = useCallback(() => {
    const canvas = canvasRef.current;
    setIsCropping(!isCropping);

    if (!isCropping) {
      const rect = new fabric.Rect({
        left: 50,
        top: 50,
        width: 100,
        height: 100,
        fill: "rgba(0,0,0,0.3)",
        stroke: "red",
        strokeWidth: 2,
        selectable: true,
        hasBorders: true,
        hasControls: true,
      });
      canvas.add(rect);
      canvas.setActiveObject(rect);
    } else {
      const activeObject = canvas.getActiveObject();
      if (activeObject && activeObject.type === "rect") {
        const croppedArea = activeObject.getBoundingRect();
        canvas.getObjects("image")[0].clipTo = function (ctx) {
          ctx.rect(croppedArea.left, croppedArea.top, croppedArea.width, croppedArea.height);
        };
        canvas.renderAll();
        canvas.remove(activeObject);
      }
      setIsCropping(false);
    }
  }, [isCropping]);

  const toggleSection = useCallback((section) => {
    setExpandedSection(expandedSection === section ? null : section);
  }, [expandedSection]);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  return (
    <div>
      <SEO
        title="Film Poster Generator"
        description="Create stunning film posters with ProducerBuddy's AI-powered poster generation tool. Design professional marketing materials in minutes."
        url="https://producerbuddy.pilomation.com/poster-generation"
        robots="noindex, nofollow"
      />
      <SubNav/>
      <div className="flex h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
        {/* Left Sidebar: Collapsible Editing Options */}
        <div className="w-1/4 p-6 bg-white shadow-xl rounded-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Poster</h3>

          {/* Text Options */}
          <button
            aria-label="Toggle text options"
            onClick={() => toggleSection("text")}
            className="w-full text-left py-3 mb-4 bg-teal-600 text-white rounded-lg shadow-md hover:bg-teal-700 transition duration-300"
          >
            Text Options
          </button>
          {expandedSection === "text" && (
            <div className="mb-4">
              <label className="block mb-2 text-gray-700 font-medium">Text: </label>
              <input
                type="text"
                value={text}
                onChange={handleTextChange}
                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          )}

          {/* Color Picker */}
          <button
            aria-label="Toggle color picker"
            onClick={() => toggleSection("color")}
            className="w-full text-left py-3 mb-4 bg-teal-600 text-white rounded-lg shadow-md hover:bg-teal-700 transition duration-300"
          >
            Color Picker
          </button>
          {expandedSection === "color" && (
            <div className="mb-4">
              <label className="block mb-2 text-gray-700 font-medium">Text Color: </label>
              <SketchPicker color={color} onChangeComplete={handleColorChange} />
            </div>
          )}

          {/* Image Settings */}
          {["brightness", "contrast", "sharpness", "rotation"].map((setting) => (
            <div key={setting}>
              <button
                aria-label={`Toggle ${setting} control`}
                onClick={() => toggleSection(setting)}
                className="w-full text-left py-3 mb-4 bg-teal-600 text-white rounded-lg shadow-md hover:bg-teal-700 transition duration-300"
              >
                {setting.charAt(0).toUpperCase() + setting.slice(1)}
              </button>
              {expandedSection === setting && (
                <div className="mb-4">
                  <label className="block mb-2 text-gray-700 font-medium">
                    {setting.charAt(0).toUpperCase() + setting.slice(1)}:
                  </label>
                  <input
                    type="range"
                    min="0"
                    max={setting === "rotation" ? "360" : "200"}
                    value={imageSettings[setting]}
                    onChange={(e) => handleImageSettingChange(setting, e.target.value)}
                    className="slider w-full"
                  />
                </div>
              )}
            </div>
          ))}

          {/* Crop Button */}
          <button
            aria-label="Toggle crop mode"
            onClick={handleCrop}
            className="w-full text-left py-3 mb-4 bg-teal-600 text-white rounded-lg shadow-md hover:bg-teal-700 transition duration-300"
          >
            {isCropping ? "Finish Cropping" : "Crop Poster"}
          </button>

          {/* Upload Image */}
          <button
            aria-label="Toggle image upload"
            onClick={() => toggleSection("upload")}
            className="w-full text-left py-3 mb-4 bg-teal-600 text-white rounded-lg shadow-md hover:bg-teal-700 transition duration-300"
          >
            Upload Image
          </button>
          {expandedSection === "upload" && (
            <div className="mb-4">
              <label className="block mb-2 text-gray-700 font-medium">Upload Image: </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
          )}
        </div>

        {/* Middle Section: Poster Generation and Recent Posters */}
        <div className="w-2/4 p-6 bg-white">
          <header className="flex items-center justify-between mb-6">
            <input
              type="text"
              placeholder="Generate posters with engaging colors for a movie"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full p-4 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ml-4"
            />
            <button
              aria-label="Generate poster"
              onClick={handleSearch}
              disabled={isLoading}
              className="bg-teal-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-teal-700 transition duration-300 disabled:opacity-50"
            >
              {isLoading ? "Generating..." : "Generate Poster"}
            </button>
          </header>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {/* Main Poster Display */}
          {selectedPosterIndex !== null && (
            <div>
              {/* Post Carousel */}
              <div className="mb-6">
                <Slider {...sliderSettings}>
                  {posters.map((poster, index) => (
                    <div key={index} className="flex justify-center items-center">
                      <img
                        src={poster.url}
                        alt={`Poster ${index + 1}`}
                        className="w-auto h-80 cursor-pointer rounded-lg shadow-lg border border-gray-300 ml-50"
                        style={{ marginLeft: '190px' }}
                        onClick={() => setSelectedPosterIndex(index)}
                      />
                    </div>
                  ))}
                </Slider>
              </div>

              {/* Post Grid Display */}
              <div className="grid grid-cols-3 gap-4">
                {posters.map((poster, index) => (
                  <div key={index} className="relative">
                    <img
                      src={poster.url}
                      alt={`Poster ${index + 1}`}
                      className={`w-full h-auto cursor-pointer rounded-lg shadow-lg border border-gray-300 ${
                        selectedPosterIndex === index ? "border-4 border-teal-600" : ""
                      }`}
                      onClick={() => setSelectedPosterIndex(index)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar: Download and Publish Options */}
        <div className="w-1/4 p-6 bg-white shadow-xl rounded-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Actions</h3>
          <div className="flex flex-col space-y-4">
            <button
              aria-label="Download poster"
              onClick={handleDownload}
              disabled={!selectedPosterIndex || isLoading}
              className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-green-700 transition duration-300 disabled:opacity-50"
            >
              Download
            </button>
            <button
              aria-label="Publish poster"
              onClick={handlePublish}
              disabled={!selectedPosterIndex || isLoading}
              className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-red-700 transition duration-300 disabled:opacity-50"
            >
              Publish
            </button>
          </div>
        </div>
      </div>

      {/* Canvas element for fabric.js */}
      <canvas
        id="poster-canvas"
        style={{ display: 'none' }}
      />
    </div>
  );
};

// PropTypes definition
PosterGenerationPage.propTypes = {
  initialPosters: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired
    })
  ),
  onPublish: PropTypes.func,
  onDownload: PropTypes.func
};

// Default props
PosterGenerationPage.defaultProps = {
  initialPosters: [],
  onPublish: () => console.log('Publish action not implemented'),
  onDownload: () => console.log('Download action not implemented')
};

export default PosterGenerationPage;
